/**
 * The authoritative Euchre state machine.
 *
 * Pure and deterministic: `applyAction(state, seat, action)` returns a new state
 * (the input is never mutated) or throws `IllegalActionError`. All randomness
 * comes from the RNG state stored inside the game.
 *
 * Hand flow:
 *   deal -> deal_options (no-ace-no-face redeal / farmer's swap, only for eligible seats)
 *        -> bidding1 (order up or pass, starting left of dealer)
 *        -> bidding2 (name a suit other than the turned-down card, or pass)
 *             all pass twice -> throw in, deal passes left (unless stick-the-dealer)
 *        -> defend_alone (each defender may defend alone)
 *        -> discard (dealer picked up the up-card in round 1 and discards one)
 *        -> playing (5 tricks) -> hand_complete | game_over
 *
 * Going alone is declared together with ordering up / calling, i.e. always
 * before the dealer discards.
 */

import {
  type Card,
  type PlayedCard,
  type Seat,
  type Suit,
  type Team,
  SEATS,
  currentWinner,
  isCard,
  isSuit,
  leftOf,
  legalPlays,
  newDeck,
  partnerOf,
  printedSuitOf,
  rankOf,
  teamOf,
} from './cards.js';
import { type RngState, seedRng, shuffleInPlace } from './rng.js';
import { type RulesConfig, DEFAULT_RULES } from './rules.js';

export type Phase =
  | 'deal_options'
  | 'bidding1'
  | 'bidding2'
  | 'defend_alone'
  | 'discard'
  | 'playing'
  | 'hand_complete'
  | 'game_over';

/** What happened to the turned-up card. */
export type UpCardStatus = 'up' | 'picked_up' | 'turned_down' | 'unused';

export type GameAction =
  | { type: 'keep_hand' }
  | { type: 'redeal' }
  | { type: 'farmer_swap'; cards: Card[] }
  | { type: 'pass' }
  | { type: 'order_up'; alone: boolean }
  | { type: 'call_trump'; suit: Suit; alone: boolean }
  | { type: 'defend_alone'; alone: boolean }
  | { type: 'discard'; card: Card }
  | { type: 'play'; card: Card };

export type GameActionType = GameAction['type'];

export interface CompletedTrick {
  number: number; // 1..5
  leader: Seat;
  cards: PlayedCard[];
  winner: Seat;
}

export interface HandResult {
  maker: Seat;
  makerTeam: Team;
  trump: Suit;
  makerAlone: boolean;
  defenderAlone: Seat | null;
  makerTricks: number;
  defenderTricks: number;
  scoringTeam: Team;
  points: number;
  outcome: 'made' | 'march' | 'euchre';
}

/** Public, human-readable record of a hand. Never contains hidden cards. */
export type LogEntry =
  | { t: 'deal'; dealer: Seat; upCard: Card; dealNumber: number }
  | { t: 'redeal_called'; seat: Seat }
  | { t: 'farmer_swap'; seat: Seat }
  | { t: 'pass'; seat: Seat; round: 1 | 2 }
  | { t: 'order_up'; seat: Seat; upCard: Card; alone: boolean }
  | { t: 'call_trump'; seat: Seat; suit: Suit; alone: boolean }
  | { t: 'defend_alone'; seat: Seat }
  | { t: 'pick_up'; seat: Seat; upCard: Card }
  | { t: 'discarded'; seat: Seat }
  | { t: 'trick'; trick: CompletedTrick }
  | { t: 'thrown_in' }
  | { t: 'result'; result: HandResult; scores: [number, number] };

export interface HandState {
  dealNumber: number;
  dealer: Seat;
  hands: [Card[], Card[], Card[], Card[]];
  /** kitty[0] is the turned-up card; kitty[1..3] are face down. */
  kitty: Card[];
  upCard: Card;
  upCardStatus: UpCardStatus;
  turn: Seat | null;
  /** Seats still to be asked in the current queued phase (deal_options / defend_alone). */
  queue: Seat[];
  farmerSwapSeat: Seat | null;
  /** The nines/tens the farmer put into the kitty (private to that seat). */
  farmerSwappedOut: Card[];
  trump: Suit | null;
  maker: Seat | null;
  callRound: 1 | 2 | null;
  makerAlone: boolean;
  defenderAlone: Seat | null;
  sittingOut: Seat[];
  /** Dealer's discard (private to the dealer). */
  discard: Card | null;
  leader: Seat | null;
  currentTrick: PlayedCard[];
  tricks: CompletedTrick[];
  tricksWon: [number, number];
  log: LogEntry[];
  result: HandResult | null;
}

export interface HandSummary {
  handNumber: number;
  dealer: Seat;
  result: HandResult;
  scoresAfter: [number, number];
}

export interface GameState {
  rules: RulesConfig;
  /** Never sent to clients. */
  rng: RngState;
  phase: Phase;
  scores: [number, number];
  /** Increments for each hand that is actually played (not thrown in). */
  handNumber: number;
  /** Increments for every deal, including redeals and throw-ins. */
  dealCount: number;
  hand: HandState;
  history: HandSummary[];
  /** Log of the previous played hand, for "last hand" review. */
  previousHandLog: LogEntry[];
  winner: Team | null;
  /** Monotonic counter; bumps on every accepted action. */
  version: number;
}

export class IllegalActionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IllegalActionError';
  }
}

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

export interface NewGameOptions {
  seed: string | number;
  rules?: Partial<RulesConfig>;
  firstDealer?: Seat;
}

export function createGame(opts: NewGameOptions): GameState {
  const rules: RulesConfig = { ...DEFAULT_RULES, ...(opts.rules ?? {}) };
  const rng = seedRng(opts.seed);
  const state: GameState = {
    rules,
    rng,
    phase: 'deal_options',
    scores: [0, 0],
    handNumber: 1,
    dealCount: 0,
    hand: undefined as unknown as HandState,
    history: [],
    previousHandLog: [],
    winner: null,
    version: 0,
  };
  deal(state, opts.firstDealer ?? 0);
  return state;
}

// ---------------------------------------------------------------------------
// Dealing
// ---------------------------------------------------------------------------

const isLowCard = (c: Card) => rankOf(c) === '9' || rankOf(c) === '10';

export function lowCards(hand: readonly Card[]): Card[] {
  return hand.filter(isLowCard);
}

export function eligibleForNoAceNoFace(rules: RulesConfig, hand: readonly Card[]): boolean {
  return rules.noAceNoFaceRedeal && hand.length === 5 && hand.every(isLowCard);
}

export function eligibleForFarmer(rules: RulesConfig, h: HandState, seat: Seat): boolean {
  return (
    rules.farmersHand &&
    h.farmerSwapSeat === null &&
    h.upCardStatus === 'up' &&
    h.kitty.length === 4 &&
    lowCards(h.hands[seat]).length >= Math.max(3, rules.farmersHandMinLowCards)
  );
}

function seatsFrom(start: Seat): Seat[] {
  return [0, 1, 2, 3].map((i) => ((start + i) % 4) as Seat);
}

/** Deal a fresh hand (mutates `state`). */
function deal(state: GameState, dealer: Seat, carryLog: LogEntry[] = []): void {
  const deck = shuffleInPlace(state.rng, newDeck());
  const hands: [Card[], Card[], Card[], Card[]] = [[], [], [], []];
  // One card at a time, starting left of the dealer.
  const order = seatsFrom(leftOf(dealer));
  let i = 0;
  for (let round = 0; round < 5; round++) {
    for (const seat of order) hands[seat].push(deck[i++]!);
  }
  const kitty = deck.slice(i);
  const upCard = kitty[0]!;
  state.dealCount += 1;
  const h: HandState = {
    dealNumber: state.dealCount,
    dealer,
    hands,
    kitty,
    upCard,
    upCardStatus: 'up',
    turn: null,
    queue: [],
    farmerSwapSeat: null,
    farmerSwappedOut: [],
    trump: null,
    maker: null,
    callRound: null,
    makerAlone: false,
    defenderAlone: null,
    sittingOut: [],
    discard: null,
    leader: null,
    currentTrick: [],
    tricks: [],
    tricksWon: [0, 0],
    // Keep the story of thrown-in / redealt hands so players can see what happened.
    log: [...carryLog.slice(-40), { t: 'deal', dealer, upCard, dealNumber: state.dealCount }],
    result: null,
  };
  state.hand = h;
  // Who gets offered a deal option?
  h.queue = order.filter((s) => eligibleForNoAceNoFace(state.rules, hands[s]) || eligibleForFarmer(state.rules, h, s));
  advanceDealOptions(state);
}

/** Move to the next eligible seat in deal_options, or start bidding. */
function advanceDealOptions(state: GameState): void {
  const h = state.hand;
  // Drop seats that are no longer eligible (e.g. the farmer swap was used).
  h.queue = h.queue.filter((s) => eligibleForNoAceNoFace(state.rules, h.hands[s]) || eligibleForFarmer(state.rules, h, s));
  const next = h.queue.shift();
  if (next !== undefined) {
    state.phase = 'deal_options';
    h.turn = next;
    return;
  }
  state.phase = 'bidding1';
  h.turn = leftOf(h.dealer);
}

// ---------------------------------------------------------------------------
// Legal actions
// ---------------------------------------------------------------------------

function combinations<T>(items: readonly T[], k: number): T[][] {
  if (k === 0) return [[]];
  if (items.length < k) return [];
  const [first, ...rest] = items;
  return [...combinations(rest, k - 1).map((c) => [first!, ...c]), ...combinations(rest, k)];
}

/** Every action `seat` may take right now (empty if it is not their turn). */
export function legalActions(state: GameState, seat: Seat): GameAction[] {
  const h = state.hand;
  if (h.turn !== seat) return [];
  switch (state.phase) {
    case 'deal_options': {
      const out: GameAction[] = [{ type: 'keep_hand' }];
      if (eligibleForNoAceNoFace(state.rules, h.hands[seat])) out.push({ type: 'redeal' });
      if (eligibleForFarmer(state.rules, h, seat)) {
        for (const cards of combinations(lowCards(h.hands[seat]), 3)) out.push({ type: 'farmer_swap', cards });
      }
      return out;
    }
    case 'bidding1':
      return [{ type: 'pass' }, { type: 'order_up', alone: false }, { type: 'order_up', alone: true }];
    case 'bidding2': {
      const out: GameAction[] = [];
      const mustCall = state.rules.stickTheDealer && seat === h.dealer;
      if (!mustCall) out.push({ type: 'pass' });
      for (const suit of ['C', 'D', 'H', 'S'] as Suit[]) {
        if (suit === printedSuitOf(h.upCard)) continue;
        out.push({ type: 'call_trump', suit, alone: false }, { type: 'call_trump', suit, alone: true });
      }
      return out;
    }
    case 'defend_alone':
      return [
        { type: 'defend_alone', alone: false },
        { type: 'defend_alone', alone: true },
      ];
    case 'discard':
      return h.hands[seat].map((card) => ({ type: 'discard', card }) as GameAction);
    case 'playing':
      return legalPlays(h.hands[seat], h.currentTrick, h.trump!).map((card) => ({ type: 'play', card }) as GameAction);
    default:
      return [];
  }
}

/** Canonical key used to compare actions (order of farmer cards does not matter). */
export function actionKey(a: GameAction): string {
  switch (a.type) {
    case 'farmer_swap':
      return `farmer_swap:${[...a.cards].sort().join(',')}`;
    case 'order_up':
      return `order_up:${a.alone}`;
    case 'call_trump':
      return `call_trump:${a.suit}:${a.alone}`;
    case 'defend_alone':
      return `defend_alone:${a.alone}`;
    case 'discard':
    case 'play':
      return `${a.type}:${a.card}`;
    default:
      return a.type;
  }
}

/** Strictly validate the *shape* of untrusted input. Returns null if malformed. */
export function parseAction(input: unknown): GameAction | null {
  if (!input || typeof input !== 'object') return null;
  const o = input as Record<string, unknown>;
  switch (o.type) {
    case 'keep_hand':
    case 'redeal':
    case 'pass':
      return { type: o.type };
    case 'farmer_swap':
      if (!Array.isArray(o.cards) || o.cards.length !== 3 || !o.cards.every(isCard)) return null;
      if (new Set(o.cards).size !== 3) return null;
      return { type: 'farmer_swap', cards: [...(o.cards as Card[])] };
    case 'order_up':
      return typeof o.alone === 'boolean' ? { type: 'order_up', alone: o.alone } : null;
    case 'call_trump':
      return isSuit(o.suit) && typeof o.alone === 'boolean' ? { type: 'call_trump', suit: o.suit, alone: o.alone } : null;
    case 'defend_alone':
      return typeof o.alone === 'boolean' ? { type: 'defend_alone', alone: o.alone } : null;
    case 'discard':
    case 'play':
      return isCard(o.card) ? { type: o.type, card: o.card } : null;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Applying actions
// ---------------------------------------------------------------------------

export function applyAction(prev: GameState, seat: Seat, action: GameAction): GameState {
  if (prev.phase === 'game_over') throw new IllegalActionError('The game is over.');
  if (prev.phase === 'hand_complete') throw new IllegalActionError('The hand is over; waiting for the next deal.');
  if (prev.hand.turn !== seat) throw new IllegalActionError("It's not your turn.");
  const key = actionKey(action);
  if (!legalActions(prev, seat).some((a) => actionKey(a) === key)) {
    throw new IllegalActionError(describeIllegal(prev, seat, action));
  }

  const state = clone(prev);
  state.version += 1;
  const h = state.hand;

  switch (action.type) {
    case 'keep_hand':
      advanceDealOptions(state);
      break;

    case 'redeal':
      h.log.push({ t: 'redeal_called', seat });
      deal(state, h.dealer, h.log); // same dealer deals again
      break;

    case 'farmer_swap': {
      const hand = h.hands[seat];
      const hidden = h.kitty.slice(1, 4);
      h.hands[seat] = [...hand.filter((c) => !action.cards.includes(c)), ...hidden];
      h.kitty = [h.kitty[0]!, ...action.cards];
      h.farmerSwapSeat = seat;
      h.farmerSwappedOut = [...action.cards];
      h.log.push({ t: 'farmer_swap', seat });
      advanceDealOptions(state);
      break;
    }

    case 'pass': {
      const round = state.phase === 'bidding1' ? 1 : 2;
      h.log.push({ t: 'pass', seat, round });
      if (seat === h.dealer) {
        if (round === 1) {
          h.upCardStatus = 'turned_down';
          state.phase = 'bidding2';
          h.turn = leftOf(h.dealer);
        } else {
          // Everyone passed twice: throw the hand in, deal passes left.
          h.log.push({ t: 'thrown_in' });
          deal(state, leftOf(h.dealer), h.log);
        }
      } else {
        h.turn = leftOf(seat);
      }
      break;
    }

    case 'order_up':
      h.log.push({ t: 'order_up', seat, upCard: h.upCard, alone: action.alone });
      setMaker(state, seat, printedSuitOf(h.upCard), 1, action.alone);
      break;

    case 'call_trump':
      h.log.push({ t: 'call_trump', seat, suit: action.suit, alone: action.alone });
      setMaker(state, seat, action.suit, 2, action.alone);
      break;

    case 'defend_alone':
      if (action.alone) {
        h.defenderAlone = seat;
        h.sittingOut.push(partnerOf(seat));
        h.log.push({ t: 'defend_alone', seat });
        h.queue = [];
      }
      advanceDefendAlone(state);
      break;

    case 'discard': {
      const hand = h.hands[seat];
      h.hands[seat] = hand.filter((c) => c !== action.card);
      h.discard = action.card;
      h.log.push({ t: 'discarded', seat });
      startPlay(state);
      break;
    }

    case 'play':
      playCard(state, seat, action.card);
      break;
  }
  return state;
}

function describeIllegal(state: GameState, seat: Seat, action: GameAction): string {
  if (action.type === 'play' && state.phase === 'playing') {
    if (!state.hand.hands[seat].includes(action.card)) return "That card isn't in your hand.";
    return 'You must follow suit.';
  }
  if (action.type === 'discard' && state.phase === 'discard') return "That card isn't in your hand.";
  if (action.type === 'call_trump' && state.phase === 'bidding2') {
    if (action.suit === printedSuitOf(state.hand.upCard)) return 'You cannot name the suit that was turned down.';
  }
  if (action.type === 'pass' && state.phase === 'bidding2') return 'Stick the dealer: the dealer must name trump.';
  return `You can't do that right now (${action.type} during ${state.phase}).`;
}

function setMaker(state: GameState, seat: Seat, trump: Suit, round: 1 | 2, alone: boolean): void {
  const h = state.hand;
  h.trump = trump;
  h.maker = seat;
  h.callRound = round;
  h.makerAlone = alone;
  if (alone) h.sittingOut.push(partnerOf(seat));
  // In round 1 the up card stays face up until the dealer actually picks it up
  // (after any defend-alone decision), see advanceDefendAlone().
  h.turn = null;
  // Ask defenders (clockwise from left of dealer) whether they want to defend alone.
  h.queue = state.rules.allowDefendAlone
    ? seatsFrom(leftOf(h.dealer)).filter((s) => teamOf(s) !== teamOf(seat))
    : [];
  advanceDefendAlone(state);
}

function advanceDefendAlone(state: GameState): void {
  const h = state.hand;
  const next = h.defenderAlone === null ? h.queue.shift() : undefined;
  if (next !== undefined) {
    state.phase = 'defend_alone';
    h.turn = next;
    return;
  }
  h.queue = [];
  // Dealer picks up the turned card if it was ordered up and the dealer is playing.
  if (h.callRound === 1) {
    if (h.sittingOut.includes(h.dealer)) {
      h.upCardStatus = 'unused';
    } else {
      h.hands[h.dealer].push(h.upCard);
      h.kitty = h.kitty.slice(1);
      h.upCardStatus = 'picked_up';
      h.log.push({ t: 'pick_up', seat: h.dealer, upCard: h.upCard });
      state.phase = 'discard';
      h.turn = h.dealer;
      return;
    }
  }
  startPlay(state);
}

function nextActiveSeat(h: HandState, from: Seat): Seat {
  let s = leftOf(from);
  while (h.sittingOut.includes(s)) s = leftOf(s);
  return s;
}

export function activeSeats(h: HandState): Seat[] {
  return SEATS.filter((s) => !h.sittingOut.includes(s));
}

function startPlay(state: GameState): void {
  const h = state.hand;
  state.phase = 'playing';
  const leader = nextActiveSeat(h, h.dealer);
  h.leader = leader;
  h.turn = leader;
  h.currentTrick = [];
}

function playCard(state: GameState, seat: Seat, card: Card): void {
  const h = state.hand;
  h.hands[seat] = h.hands[seat].filter((c) => c !== card);
  h.currentTrick.push({ seat, card });
  if (h.currentTrick.length < activeSeats(h).length) {
    h.turn = nextActiveSeat(h, seat);
    return;
  }
  const winner = currentWinner(h.currentTrick, h.trump!)!;
  const trick: CompletedTrick = { number: h.tricks.length + 1, leader: h.leader!, cards: h.currentTrick, winner };
  h.tricks.push(trick);
  h.tricksWon[teamOf(winner)] += 1;
  h.log.push({ t: 'trick', trick });
  h.currentTrick = [];
  if (h.tricks.length < 5) {
    h.leader = winner;
    h.turn = winner;
    return;
  }
  scoreHand(state);
}

export function computeResult(rules: RulesConfig, h: HandState): HandResult {
  const maker = h.maker!;
  const makerTeam = teamOf(maker);
  const makerTricks = h.tricksWon[makerTeam];
  const defenderTricks = 5 - makerTricks;
  let points: number;
  let scoringTeam: Team;
  let outcome: HandResult['outcome'];
  if (makerTricks === 5) {
    outcome = 'march';
    scoringTeam = makerTeam;
    points = h.makerAlone ? 4 : 2;
  } else if (makerTricks >= 3) {
    outcome = 'made';
    scoringTeam = makerTeam;
    points = 1;
  } else {
    outcome = 'euchre';
    scoringTeam = (1 - makerTeam) as Team;
    points = h.defenderAlone !== null ? rules.loneDefenderEuchrePoints : 2;
  }
  return {
    maker,
    makerTeam,
    trump: h.trump!,
    makerAlone: h.makerAlone,
    defenderAlone: h.defenderAlone,
    makerTricks,
    defenderTricks,
    scoringTeam,
    points,
    outcome,
  };
}

function scoreHand(state: GameState): void {
  const h = state.hand;
  const result = computeResult(state.rules, h);
  h.result = result;
  h.turn = null;
  h.leader = null;
  state.scores[result.scoringTeam] += result.points;
  h.log.push({ t: 'result', result, scores: [...state.scores] as [number, number] });
  state.history.push({
    handNumber: state.handNumber,
    dealer: h.dealer,
    result,
    scoresAfter: [...state.scores] as [number, number],
  });
  if (state.scores[result.scoringTeam] >= state.rules.targetScore) {
    state.phase = 'game_over';
    state.winner = result.scoringTeam;
  } else {
    state.phase = 'hand_complete';
  }
}

/** After a hand is scored, deal the next one (deal passes to the left). */
export function startNextHand(prev: GameState): GameState {
  if (prev.phase !== 'hand_complete') throw new IllegalActionError('The current hand is not finished.');
  const state = clone(prev);
  state.version += 1;
  state.handNumber += 1;
  state.previousHandLog = state.hand.log;
  deal(state, leftOf(state.hand.dealer));
  return state;
}

/** Seat whose input the game is waiting for, or null. */
export function waitingOn(state: GameState): Seat | null {
  if (state.phase === 'hand_complete' || state.phase === 'game_over') return null;
  return state.hand.turn;
}
